<template>
  <div class="pb-72">
    <Skeleton v-if="isInitialLoading" />
    <div v-else>
      <RevContainer>
        <div class="mx-auto max-w-[736px]">
          <div class="mx-auto mt-16">
            <div class="grid grid-cols-2 gap-x-24 lg:gap-x-40">
              <ProductCard
                v-for="(product, index) in stableProducts"
                :key="`card-${product.productId}`"
                :loading="switchingProductIndex === index && pending"
                :product
                @product-click="onProductClick(index, $event)"
              />
            </div>
            <Tabs :anchors class="mt-32" />
            <TableContent
              class="mt-32"
              :loading-column-index="pending ? switchingProductIndex : null"
              :product-names="stableProductNames"
              :ratings="stableRatings"
              :table-data="stableTableData"
              @reviews-click="onReviewsClick"
            />
          </div>
        </div>
      </RevContainer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useState } from '#imports'
import { computed, toRef } from 'vue'

import { RevContainer } from '@ds/components/Container'

import { useAddCanonicalLink } from '~/scopes/seo/composables/useAddCanonicalLink'

import { useCompareClickTracking } from '../../../composables/useCompareClickTracking/useCompareClickTracking'
import { useEnrichResultsPage } from '../../../composables/useEnrichResultsPage/useEnrichResultsPage'
import type { ComparisonData } from '../../../composables/useEnrichResultsPage/useEnrichResultsPage.types'
import { useModelsComparisonHead } from '../../../composables/useModelsComparisonHeadV2/useModelsComparisonHeadV2'
import { useStableValue } from '../../../composables/useStableValue'
import ProductCard from '../ProductCard/ProductCard.vue'
import Skeleton from '../Skeleton/Skeleton.vue'
import TableContent from '../TableContent/TableContent.vue'
import Tabs from '../Tabs/Tabs.vue'

const props = defineProps<{
  comparisonData: ComparisonData | null | undefined
  comparisonPending: boolean
}>()

const switchingProductIndex = useState<0 | 1 | null>(
  'comparison-switching-index',
  () => null,
)

const { products, ratings, tableData, anchors, pending } = useEnrichResultsPage(
  toRef(props, 'comparisonData'),
  toRef(props, 'comparisonPending'),
)

const isReady = computed(() => !pending.value)
const stableProducts = useStableValue(products, isReady)
const stableRatings = useStableValue(ratings, isReady)
const stableTableData = useStableValue(tableData, isReady)

const isInitialLoading = computed(
  () => pending.value && stableProducts.value.length === 0,
)

const { trackProductClick, trackReviewsClick } = useCompareClickTracking()

function onProductClick(index: number, payload: { ctaLabel: 'see_options' }) {
  const product = stableProducts.value[index]
  if (!product) return

  trackProductClick({
    product,
    position: index + 1,
    listName: 'compare_hero',
    ctaLabel: payload.ctaLabel,
  })
}

function onReviewsClick({ position }: { position: number }) {
  const product = stableProducts.value[position - 1]
  const rating = stableRatings.value[position - 1]
  if (!product || !rating) return

  trackReviewsClick({ product, rating, position })
}

const productNames = computed(() =>
  products.value.length >= 2
    ? [products.value[0].title, products.value[1].title]
    : null,
)
useModelsComparisonHead(productNames)

const stableProductNames = computed(() =>
  stableProducts.value.length >= 2
    ? stableProducts.value.map((p) => p.title)
    : undefined,
)

useAddCanonicalLink()
</script>
